"use client"

import { cn } from "@/lib/utils"
import { BarChart3 } from "lucide-react"

export interface BreakdownItem {
  label: string
  count: number
  color?: string
}

const DEFAULT_COLORS = [
  "#3b82f6", // blue
  "#f59e0b", // amber
  "#10b981", // emerald
  "#8b5cf6", // violet
  "#ef4444", // red
  "#06b6d4", // cyan
  "#ec4899", // pink
  "#6b7280", // gray
]

export function BreakdownCard({
  title,
  total,
  unit = "条",
  items,
  className,
}: {
  title?: string
  total?: number
  unit?: string
  items: BreakdownItem[]
  className?: string
}) {
  const computedTotal = total ?? items.reduce((a, b) => a + b.count, 0)
  const maxCount = Math.max(...items.map(i => i.count))

  return (
    <div className={cn(
      "rounded-xl border border-border/60 bg-card/60 backdrop-blur-sm overflow-hidden",
      className,
    )}>
      {/* Header */}
      <div className="px-4 py-2.5 border-b border-border/40 bg-secondary/20 flex items-center gap-2">
        <BarChart3 className="h-4 w-4 text-primary/70" />
        <span className="text-xs font-semibold text-foreground">{title || "分类统计"}</span>
        <span className="text-micro px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-medium ml-auto">
          共 {computedTotal} {unit}
        </span>
      </div>

      {/* Stacked bar */}
      <div className="px-4 pt-3 pb-1">
        <div className="flex h-2.5 rounded-full overflow-hidden bg-secondary/40">
          {items.map((item, i) => {
            const pct = computedTotal > 0 ? (item.count / computedTotal) * 100 : 0
            const color = item.color || DEFAULT_COLORS[i % DEFAULT_COLORS.length]
            return (
              <div
                key={i}
                className="h-full transition-all duration-500 ease-out animate-in fade-in"
                style={{
                  width: `${pct}%`,
                  backgroundColor: color,
                  animationDelay: `${i * 80}ms`,
                }}
              />
            )
          })}
        </div>
      </div>

      {/* Items */}
      <div className="px-4 py-2.5 space-y-1.5">
        {items.map((item, i) => {
          const pct = computedTotal > 0 ? (item.count / computedTotal) * 100 : 0
          const barPct = maxCount > 0 ? (item.count / maxCount) * 100 : 0
          const color = item.color || DEFAULT_COLORS[i % DEFAULT_COLORS.length]

          return (
            <div
              key={i}
              className="flex items-center gap-2.5 animate-in fade-in slide-in-from-left-1"
              style={{ animationDelay: `${i * 50}ms`, animationFillMode: "both" }}
            >
              {/* Color dot */}
              <div
                className="h-2 w-2 rounded-full shrink-0"
                style={{ backgroundColor: color }}
              />
              {/* Label */}
              <span className="text-xs text-foreground/80 w-14 shrink-0 truncate">{item.label}</span>
              {/* Bar */}
              <div className="flex-1 h-1.5 rounded-full bg-secondary/40 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-700 ease-out"
                  style={{ width: `${barPct}%`, backgroundColor: color, opacity: 0.65 }}
                />
              </div>
              {/* Count + percentage */}
              <span className="text-caption font-mono text-muted-foreground tabular-nums w-12 text-right shrink-0">
                {item.count}
              </span>
              <span className="text-micro text-muted-foreground/60 w-8 text-right shrink-0">
                {pct.toFixed(0)}%
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}
